import { IconCheck } from '@myplayer/ui';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils.js';
import { useFloatingMenuDismiss } from './context-menu.js';
import { Button } from './primitives.js';

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>): ReactElement {
  return (
    <div
      className={cn(
        'relative flex min-w-0 flex-col rounded-card border border-border bg-surface text-fg shadow-card-base transition-all duration-200 hover:border-border-medium',
        className,
      )}
      {...props}
    />
  );
}

const cardIconVariants = cva(
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full [&_svg]:h-[19px] [&_svg]:w-[19px]',
  {
    variants: {
      variant: {
        default: 'bg-surface-muted text-fg-secondary',
        brand: 'bg-surface-muted text-fg-secondary',
        success: 'bg-surface-muted text-fg-secondary',
        danger: 'bg-surface-muted text-fg-secondary',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface CardIconProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardIconVariants> {}

export function CardIcon({ className, variant, ...props }: CardIconProps): ReactElement {
  return (
    <div aria-hidden="true" className={cn(cardIconVariants({ variant }), className)} {...props} />
  );
}

export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>): ReactElement {
  return (
    <div
      className={cn(
        'flex flex-wrap items-start justify-between gap-3 px-4 pt-4 pb-3.5 sm:px-5 [&>*:first-child]:min-w-0 [&>*:first-child]:flex-1 [&>*:nth-child(2)]:shrink-0',
        className,
      )}
      {...props}
    />
  );
}

export function CardTitle({
  className,
  ...props
}: HTMLAttributes<HTMLHeadingElement>): ReactElement {
  return (
    <h2
      className={cn('text-[15px] font-semibold leading-snug tracking-[-0.1px] text-fg', className)}
      {...props}
    />
  );
}

export function CardDescription({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>): ReactElement {
  return (
    <p
      className={cn('mt-0.5 text-[13px] leading-relaxed text-fg-secondary', className)}
      {...props}
    />
  );
}

export function CardContent({ className, ...props }: HTMLAttributes<HTMLDivElement>): ReactElement {
  return (
    <div className={cn('min-w-0 flex-1 px-4 pb-4 pt-0 sm:px-5 sm:pb-5', className)} {...props} />
  );
}

export interface CardRowProps extends HTMLAttributes<HTMLDivElement> {
  readonly label: ReactNode;
  readonly children: ReactNode;
}

export function CardRow({ label, children, className, ...props }: CardRowProps): ReactElement {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 py-3 text-[13px]',
        className,
      )}
      {...props}
    >
      <span className="shrink-0 font-semibold text-fg-secondary">{label}</span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2 text-right font-medium text-fg break-words">
        {children}
      </div>
    </div>
  );
}

export function Skeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>): ReactElement {
  return (
    <div
      aria-hidden="true"
      className={cn('animate-pulse rounded-control bg-subtle', className)}
      {...props}
    />
  );
}

export interface ActionOverflowMenuProps {
  readonly label?: string | undefined;
  readonly size?: 'sm' | 'xs' | undefined;
  readonly disabled?: boolean | undefined;
  readonly 'data-testid'?: string | undefined;
  readonly children: ReactNode;
}

function computeOverflowMenuStyle(el: HTMLElement | null): CSSProperties {
  if (!el || typeof window === 'undefined') return {};
  const rect = el.getBoundingClientRect();
  const viewportW = window.innerWidth || 1024;
  const viewportH = window.innerHeight || 768;
  const margin = 8;
  const rightOffset = Math.max(margin, viewportW - rect.right);
  const spaceBelow = viewportH - rect.bottom - margin;
  const spaceAbove = rect.top - margin;
  const openUpward = spaceBelow < 200 && spaceAbove > spaceBelow;

  return {
    position: 'fixed',
    zIndex: 80,
    right: `${Math.round(rightOffset)}px`,
    ...(openUpward
      ? { bottom: `${Math.round(Math.max(margin, viewportH - rect.top + 6))}px` }
      : { top: `${Math.round(rect.bottom + 6)}px` }),
  };
}

export function ActionOverflowMenu({
  label = 'More actions',
  size = 'sm',
  disabled = false,
  'data-testid': dataTestId,
  children,
}: ActionOverflowMenuProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const containerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const closeMenu = useCallback(() => setOpen(false), []);

  useFloatingMenuDismiss(open, closeMenu, [containerRef, menuRef]);

  useLayoutEffect(() => {
    if (!open || typeof window === 'undefined') {
      setMenuStyle({});
      return;
    }
    const updatePosition = (): void => {
      setMenuStyle(computeOverflowMenuStyle(containerRef.current));
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  const effectiveStyle = open
    ? menuStyle.position !== undefined
      ? menuStyle
      : computeOverflowMenuStyle(containerRef.current)
    : undefined;
  const usePortal = open && effectiveStyle?.position === 'fixed' && typeof document !== 'undefined';

  const menuElement = (
    <div
      ref={menuRef}
      onClick={() => setOpen(false)}
      style={effectiveStyle}
      className={
        open
          ? cn(
              'z-50 flex min-w-[180px] flex-col gap-1 rounded-control border border-border bg-surface p-1.5 shadow-modal [&>button]:w-full [&>button]:justify-start',
              effectiveStyle?.position === 'fixed' ? 'fixed' : 'absolute right-0 top-full mt-1.5',
            )
          : 'sr-only'
      }
    >
      {children}
    </div>
  );

  return (
    <div ref={containerRef} className="relative inline-flex items-center">
      <Button
        type="button"
        variant="secondary"
        size={size === 'xs' ? 'icon-xs' : 'icon'}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={dataTestId}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className="shrink-0 text-[16px] font-bold leading-none"
      >
        <span aria-hidden="true">&#8943;</span>
      </Button>
      {usePortal ? createPortal(menuElement, document.body) : menuElement}
    </div>
  );
}

export interface VirtualizedSliceOptions<T> {
  readonly items: readonly T[];
  readonly scrollTop: number;
  readonly viewportHeight: number;
  readonly rowHeight: number;
  readonly overscan?: number;
}

export interface VirtualizedSliceResult<T> {
  readonly visibleItems: readonly T[];
  readonly startIndex: number;
  readonly endIndex: number;
  readonly topSpacerHeight: number;
  readonly bottomSpacerHeight: number;
  readonly totalHeight: number;
}

export function computeVirtualizedSlice<T>({
  items,
  scrollTop,
  viewportHeight,
  rowHeight,
  overscan = 6,
}: VirtualizedSliceOptions<T>): VirtualizedSliceResult<T> {
  const count = items.length;
  const safeRowHeight = Math.max(1, rowHeight);
  const totalHeight = count * safeRowHeight;
  if (count === 0) {
    return {
      visibleItems: [],
      startIndex: 0,
      endIndex: 0,
      topSpacerHeight: 0,
      bottomSpacerHeight: 0,
      totalHeight: 0,
    };
  }
  const effectiveViewport = viewportHeight > 0 ? viewportHeight : 640;
  const firstVisible = Math.floor(Math.max(0, scrollTop) / safeRowHeight);
  const visibleRowCount = Math.ceil(effectiveViewport / safeRowHeight) + overscan * 2;
  const startIndex = Math.max(0, firstVisible - overscan);
  const endIndex = Math.min(count, startIndex + visibleRowCount);
  const topSpacerHeight = startIndex * safeRowHeight;
  const bottomSpacerHeight = Math.max(0, (count - endIndex) * safeRowHeight);

  return {
    visibleItems: items.slice(startIndex, endIndex),
    startIndex,
    endIndex,
    topSpacerHeight,
    bottomSpacerHeight,
    totalHeight,
  };
}

export interface ToastNotificationProps extends HTMLAttributes<HTMLDivElement> {
  readonly message: ReactNode | null | undefined;
  readonly onDismiss?: (() => void) | undefined;
  readonly autoHideMs?: number | undefined;
  readonly 'data-testid'?: string | undefined;
}

export function ToastNotification({
  message,
  onDismiss,
  autoHideMs = 4500,
  className,
  role = 'status',
  ...props
}: ToastNotificationProps): ReactElement | null {
  useEffect(() => {
    if (message === null || message === undefined || message === '' || !onDismiss) return;
    if (autoHideMs <= 0) return;
    const timer = window.setTimeout(() => {
      onDismiss();
    }, autoHideMs);
    return () => window.clearTimeout(timer);
  }, [message, onDismiss, autoHideMs]);

  if (message === null || message === undefined || message === '') return null;

  const toastElement = (
    <div
      role={role}
      aria-live="polite"
      className={cn(
        'fixed bottom-20 right-4 z-[90] flex max-w-[min(440px,calc(100vw-2rem))] items-center justify-between gap-3 rounded-card border border-status-valid-border bg-surface px-4 py-3 text-[12px] font-medium text-fg shadow-modal md:bottom-5 md:right-5',
        className,
      )}
      {...props}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-status-valid-bg text-status-valid">
          <IconCheck className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 break-words leading-snug">{message}</div>
      </div>
      {onDismiss !== undefined && (
        <Button
          type="button"
          variant="ghost"
          size="xs"
          onClick={onDismiss}
          className="shrink-0 text-fg-secondary hover:text-fg"
        >
          Dismiss
        </Button>
      )}
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(toastElement, document.body) : toastElement;
}
